import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { GlassCard } from "@/components/common/GlassCard";
import { DraftTemplateSelect } from "@/components/form/DraftTemplateSelect";
import { JurisdictionSelect } from "@/components/form/JurisdictionSelect";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { fetchDraftLegalRoles } from "@/lib/api/drafts";
import { SourceDocumentsUploader } from "./SourceDocumentsUploader";
import type { DraftLegalRole, DraftWizardData } from "@/types/drafting";

interface Step1SetupProps {
  data: DraftWizardData;
  onChange: <K extends keyof DraftWizardData>(field: K, value: DraftWizardData[K]) => void;
}

export function Step1Setup({ data, onChange }: Step1SetupProps) {
  const [roles, setRoles] = useState<DraftLegalRole[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setIsLoading(true);
      try {
        const roleRows = await fetchDraftLegalRoles();
        if (!cancelled) {
          setRoles(roleRows);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="space-y-6">
      <GlassCard className="border-[var(--lm-border-card)] bg-white dark:border-glass-border dark:bg-slate-950/45">
        <div className="mb-4">
          <h2 className="text-base font-semibold text-[var(--lm-foreground-strong)] dark:text-white">
            Agreement template
          </h2>
          <p className="text-xs text-[var(--lm-muted)] dark:text-slate-400">
            Choose a drafting template (structure and defaults). This is separate from contract-review
            contract types.
          </p>
        </div>
        <DraftTemplateSelect
          value={data.templateId}
          onChange={(template) => {
            onChange("templateId", template?.id ?? null);
            onChange("contractType", template?.value ?? "");
          }}
          hideLabel
        />
        <p className="mt-3 text-xs text-[var(--lm-muted)] dark:text-slate-500">
          Manage templates under{" "}
          <Link to="/drafting/templates" className="text-primary hover:underline">
            Templates
          </Link>
          .
        </p>
      </GlassCard>

      <GlassCard className="border-[var(--lm-border-card)] bg-white dark:border-glass-border dark:bg-slate-950/45">
        <div className="mb-4">
          <h2 className="text-base font-semibold text-[var(--lm-foreground-strong)] dark:text-white">
            Legal role
          </h2>
          <p className="text-xs text-[var(--lm-muted)] dark:text-slate-400">
            Choose the drafting perspective for AI assistance.
          </p>
        </div>
        {isLoading ? (
          <p className="text-sm text-[var(--lm-muted)]">Loading legal roles...</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {roles.map((role) => {
              const selected = data.legalRoleId === role.id;
              return (
                <button
                  key={role.id}
                  type="button"
                  onClick={() => onChange("legalRoleId", role.id)}
                  className={cn(
                    "rounded-full border px-3 py-1.5 text-sm transition-all",
                    selected
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-[var(--lm-border-subtle)] bg-[var(--surface-sunken)] text-[var(--lm-foreground-strong)] hover:border-primary/40 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-200"
                  )}
                >
                  {role.name}
                </button>
              );
            })}
          </div>
        )}
        <p className="mt-3 text-xs text-[var(--lm-muted)] dark:text-slate-500">
          System defaults can be customized under{" "}
          <Link to="/drafting/legal-roles" className="text-primary hover:underline">
            Legal Roles
          </Link>
          .
        </p>
      </GlassCard>

      <GlassCard className="border-[var(--lm-border-card)] bg-white dark:border-glass-border dark:bg-slate-950/45">
        <div className="mb-4 grid gap-4 sm:grid-cols-2 sm:items-start">
          <div>
            <label className="mb-2 block text-sm font-medium text-[var(--lm-foreground-strong)] dark:text-slate-200">
              Matter title
            </label>
            <Input
              value={data.title}
              onChange={(e) => onChange("title", e.target.value)}
              placeholder="e.g. Executive Employment Agreement — Jane Doe"
            />
          </div>
          <JurisdictionSelect value={data.jurisdiction} onChange={(v) => onChange("jurisdiction", v)} />
        </div>
        <SourceDocumentsUploader
          documents={data.sourceDocuments}
          onChange={(docs) => onChange("sourceDocuments", docs)}
        />
      </GlassCard>
    </div>
  );
}
